import { AppIcon } from '@/components/ui/app-icon';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  iconEye,
  iconEyeSlash,
  iconLinkedin,
  iconLock,
  iconMobileScreenButton,
} from '@/lib/icons';
import {
  useCheckLinkedInCheckpointMutation,
  useConnectLinkedInMutation,
  useSolveLinkedInCheckpointMutation,
  type EngageMailbox,
  type LinkedInCheckpointType,
  type LinkedInConnectResult,
} from '@/store/features/engage.feature';
import { PlanLocked } from '@/containers/playbooks/custom-builder/config/plan-locked.component';
import { apiErrorCode, apiErrorMessage } from '../api-error.util';

/** LinkedIn's sign-in attempt lasts this long; after it, start again. */
const SIGN_IN_WINDOW_MS = 5 * 60_000;
const POLL_MS = 5000;
const TIMED_OUT = 'The LinkedIn sign-in timed out. Start again.';
const NEEDS_PRO = 'LinkedIn connects on Pro and up.';

const accountName = (account: EngageMailbox) =>
  account.display_name || account.email_address;

/**
 * Connect (or reconnect) a LinkedIn account with its email and password, then
 * walk through whatever check LinkedIn asks for. The password lives in state
 * only until it is submitted.
 */
export function LinkedInConnectDialog({
  open,
  account,
  onOpenChange,
  onConnected,
}: {
  open: boolean;
  /** Set to reconnect this account; null adds a new one. */
  account: EngageMailbox | null;
  onOpenChange: (open: boolean) => void;
  onConnected?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        {/* Unmounts on close: fields, session and polling go with it. */}
        <ConnectFlow
          account={account}
          onDone={() => {
            onConnected?.();
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ConnectFlow({
  account,
  onDone,
}: {
  account: EngageMailbox | null;
  onDone: () => void;
}) {
  const [connect, { reset: forgetConnect }] = useConnectLinkedInMutation();
  const [solve] = useSolveLinkedInCheckpointMutation();
  const [check] = useCheckLinkedInCheckpointMutation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [checkpoint, setCheckpoint] = useState<{
    type: LinkedInCheckpointType;
    session: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [needsPro, setNeedsPro] = useState(false);
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(0);

  async function run(
    request: () => Promise<LinkedInConnectResult>,
    showBusy = true
  ) {
    if (showBusy) setBusy(true);
    setError('');
    try {
      const result = await request();
      if (result.status === 'connected') {
        toast.success(`LinkedIn connected: ${accountName(result.account)}`);
        onDone();
        return;
      }
      // A new check replaces the current one, with its new session.
      setCheckpoint({ type: result.checkpoint, session: result.session });
      setCode('');
    } catch (err) {
      const code = apiErrorCode(err);
      // The plan's refusal: the routes' own check, or the sender admission.
      const belowPro =
        code === 'linkedin_requires_pro' || code === 'paid_plan_required';
      if (code === 'session_expired' || belowPro) setCheckpoint(null);
      setNeedsPro(belowPro);
      setError(
        belowPro
          ? NEEDS_PRO
          : apiErrorMessage(err) ||
              'Could not connect LinkedIn. Please try again.'
      );
    } finally {
      if (showBusy) setBusy(false);
    }
  }

  function submitCredentials(event: FormEvent) {
    event.preventDefault();
    const body = {
      username: username.trim(),
      password,
      // Reconnect into the same row, turned off or not, so sequences and plays
      // that point at it keep working.
      ...(account ? { account_id: account.id } : {}),
    };
    // Out of state the moment it is sent; a failed try is typed again.
    setPassword('');
    setShowPassword(false);
    startedAt.current = Date.now();
    void run(() =>
      connect(body)
        .unwrap()
        // The hook keeps the last call's arguments until reset.
        .finally(forgetConnect)
    );
  }

  function sendCode(value: string) {
    if (!checkpoint || !value) return;
    const { session } = checkpoint;
    void run(() => solve({ session, code: value }).unwrap());
  }

  // IN_APP_VALIDATION finishes on the phone: ask every few seconds. Each
  // answer sets a new checkpoint object, which schedules the next ask; an
  // error, a connect or closing the dialog ends it.
  useEffect(() => {
    if (checkpoint?.type !== 'IN_APP_VALIDATION') return;
    const { session } = checkpoint;
    const timer = window.setTimeout(() => {
      if (Date.now() - startedAt.current >= SIGN_IN_WINDOW_MS) {
        setCheckpoint(null);
        setError(TIMED_OUT);
        return;
      }
      void run(() => check({ session }).unwrap(), false);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
    // `run` is recreated every render; the checkpoint is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoint]);

  const title = account
    ? `Reconnect ${accountName(account)}`
    : 'Connect LinkedIn';
  const errorLine = error && (
    <>
      <p role='alert' className='text-sm text-destructive'>
        {error}
      </p>
      {needsPro && <PlanLocked />}
    </>
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <AppIcon
            icon={iconLinkedin}
            className='text-primary'
            style={{ fontSize: 16 }}
          />
          {title}
        </DialogTitle>
        <DialogDescription>
          {checkpoint
            ? 'LinkedIn wants to confirm it’s you.'
            : 'Your sequences take LinkedIn steps as this account.'}
        </DialogDescription>
      </DialogHeader>

      {!checkpoint ? (
        <form onSubmit={submitCredentials} className='space-y-4'>
          <div className='flex items-start gap-2.5 rounded-lg border border-gray-200 bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground dark:border-white/[0.08]'>
            <AppIcon
              icon={iconLock}
              className='mt-0.5 shrink-0 text-foreground'
              style={{ fontSize: 12 }}
            />
            <p>
              Your LinkedIn email and password are not saved. They’re only used
              once to sign in to LinkedIn and connect the account.
            </p>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='linkedin-username'>LinkedIn email</Label>
            <Input
              id='linkedin-username'
              type='email'
              autoComplete='username'
              required
              maxLength={320}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='linkedin-password'>LinkedIn password</Label>
            <div className='relative'>
              <Input
                id='linkedin-password'
                type={showPassword ? 'text' : 'password'}
                autoComplete='current-password'
                required
                maxLength={512}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className='pr-9'
              />
              <button
                type='button'
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((shown) => !shown)}
                className='absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-[10px] text-muted-foreground outline-hidden hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring'
              >
                <AppIcon
                  icon={showPassword ? iconEyeSlash : iconEye}
                  style={{ fontSize: 13 }}
                />
              </button>
            </div>
          </div>
          {errorLine}
          <DialogFooter>
            <Button type='submit' disabled={busy}>
              {busy ? 'Connecting…' : account ? 'Reconnect' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
      ) : checkpoint.type === 'IN_APP_VALIDATION' ? (
        <div className='space-y-4'>
          <div className='flex items-start gap-3 text-sm'>
            <AppIcon
              icon={iconMobileScreenButton}
              className='mt-0.5 shrink-0 text-primary'
              style={{ fontSize: 18 }}
            />
            <div>
              <p>
                Open the LinkedIn app on your phone and approve the sign-in.
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                This connects on its own once you approve it.
              </p>
            </div>
          </div>
          {errorLine}
          <DialogFooter>
            <Button
              disabled={busy}
              onClick={() =>
                void run(() => check({ session: checkpoint.session }).unwrap())
              }
            >
              {busy ? 'Checking…' : 'I’ve approved it'}
            </Button>
          </DialogFooter>
        </div>
      ) : checkpoint.type === 'CAPTCHA' ? (
        <div className='space-y-4'>
          <p className='text-sm'>
            LinkedIn wants a security check we can’t show here. Sign in to
            LinkedIn in your browser once, then try again.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setCheckpoint(null);
                setError('');
              }}
            >
              Back
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            sendCode(code.trim());
          }}
          className='space-y-4'
        >
          <div className='space-y-1.5'>
            <Label htmlFor='linkedin-code'>
              {checkpoint.type === 'PHONE_REGISTER'
                ? 'Phone number'
                : 'Verification code'}
            </Label>
            <p className='text-xs text-muted-foreground'>
              {checkpoint.type === 'PHONE_REGISTER'
                ? 'LinkedIn wants a phone number on this account. Country code in brackets, then the number.'
                : checkpoint.type === '2FA'
                  ? 'Enter the code from your authenticator app or text message.'
                  : 'Enter the code LinkedIn sent to your email or phone.'}
            </p>
            {checkpoint.type === 'PHONE_REGISTER' ? (
              <Input
                id='linkedin-code'
                key='phone'
                type='tel'
                autoComplete='tel'
                placeholder='(+1)5551234567'
                autoFocus
                required
                maxLength={64}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            ) : (
              <Input
                id='linkedin-code'
                key='code'
                inputMode='numeric'
                autoComplete='one-time-code'
                autoFocus
                required
                maxLength={64}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            )}
          </div>
          {errorLine}
          <DialogFooter className='items-center sm:justify-between'>
            {checkpoint.type !== 'PHONE_REGISTER' ? (
              <Button
                type='button'
                variant='link'
                className='h-auto px-0 text-xs'
                disabled={busy}
                onClick={() => sendCode('TRY_ANOTHER_WAY')}
              >
                Try another way
              </Button>
            ) : (
              <span />
            )}
            <Button type='submit' disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          </DialogFooter>
        </form>
      )}
    </>
  );
}
